import React, { useState, useEffect, useRef } from 'react';
import { Upload, Video, FileText, Image, Trash2, Edit2, X, Plus, Filter, Search, FolderOpen, Check, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminContent = () => {
  const [contents, setContents] = useState([]);
  const [stats, setStats] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [filter, setFilter] = useState({ type: '', category: '', level: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [editingContent, setEditingContent] = useState(null);
  
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('session_token');
  const headers = { Authorization: `Bearer ${token}` };

  // Form state para upload
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    category: 'entrenamiento',
    level: 'free',
    access_options: ['free'],  // opciones: 'free', 'paid', 'premium_only'
    price: '',
    discount_code: '',
    discount_percentage: '',
    program_ids: [],
    file: null
  });

  useEffect(() => {
    fetchContents();
    fetchStats();
    fetchPrograms();
  }, [filter]);

  const fetchPrograms = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/programs`, { headers });
      const fetched = res.data || [];
      setPrograms([
        { program_id: '__FREE__', name: '🌍 Acceso Gratuito (Abierto)' },
        ...fetched,
      ]);
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  };

  const resolveAccessPayload = (selectedProgramIds = []) => {
    const arr = selectedProgramIds || [];
    const hasFree = arr.includes('__FREE__');
    const realIds = arr.filter(p => p && p !== '__FREE__');
    if (hasFree) {
      return { program_ids: [], levels: ['free'], access_options: ['free'], active: true };
    }
    if (realIds.length > 0) {
      return { program_ids: realIds, levels: ['premium'], access_options: ['premium_only'], active: true };
    }
    return { program_ids: [], levels: ['free'], access_options: ['free'], active: false };
  };

  const fetchContents = async () => {
    try {
      let url = `${BACKEND_URL}/api/content`;
      const params = new URLSearchParams();
      if (filter.type) params.append('content_type', filter.type);
      if (filter.category) params.append('category', filter.category);
      if (filter.level) params.append('level', filter.level);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, { headers });
      setContents(response.data);
    } catch (error) {
      console.error('Error fetching contents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/content/stats/summary`, { headers });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm({ ...uploadForm, file, title: uploadForm.title || file.name.split('.')[0] });
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.title) {
      toast.error('Selecciona un archivo y agrega un título');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const access = resolveAccessPayload(uploadForm.program_ids);

    const formData = new FormData();
    formData.append('file', uploadForm.file);
    formData.append('title', uploadForm.title);
    formData.append('description', uploadForm.description);
    formData.append('category', uploadForm.category);
    formData.append('level', access.levels[0]);
    formData.append('levels', access.levels.join(','));
    formData.append('access_options', access.access_options.join(','));
    formData.append('active', access.active ? 'true' : 'false');
    if (access.program_ids.length > 0) formData.append('program_ids', access.program_ids.join(','));
    if (uploadForm.price) formData.append('price', uploadForm.price);
    if (uploadForm.discount_code) formData.append('discount_code', uploadForm.discount_code);
    if (uploadForm.discount_percentage) formData.append('discount_percentage', uploadForm.discount_percentage);

    try {
      await axios.post(`${BACKEND_URL}/api/content/upload`, formData, {
        headers: {
          ...headers,
          
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });

      toast.success('¡Contenido subido exitosamente!');
      setShowUploadModal(false);
      setUploadForm({ title: '', description: '', category: 'entrenamiento', level: 'free', access_options: ['free'], price: '', discount_code: '', discount_percentage: '', program_ids: [], file: null });
      fetchContents();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir el archivo');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const toggleAccessOption = (option) => {
    const current = uploadForm.access_options;
    if (current.includes(option)) {
      setUploadForm({ ...uploadForm, access_options: current.filter(o => o !== option) });
    } else {
      setUploadForm({ ...uploadForm, access_options: [...current, option] });
    }
  };

  const handleDelete = async (contentId) => {
    if (!window.confirm('¿Estás segura de eliminar este contenido?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/content/${contentId}`, { headers });
      toast.success('Contenido eliminado');
      fetchContents();
      fetchStats();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleUpdate = async () => {
    if (!editingContent) return;

    try {
      const access = resolveAccessPayload(editingContent.program_ids);
      await axios.put(`${BACKEND_URL}/api/content/${editingContent.content_id}`, {
        title: editingContent.title,
        description: editingContent.description,
        category: editingContent.category,
        level: access.levels[0],
        levels: access.levels,
        access_options: access.access_options,
        program_ids: access.program_ids,
        active: access.active
      }, { headers });

      toast.success('Contenido actualizado');
      setEditingContent(null);
      fetchContents();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const toggleProgramSelection = (formState, setFormState, programId) => {
    const current = formState.program_ids || [];
    if (current.includes(programId)) {
      setFormState({ ...formState, program_ids: current.filter(p => p !== programId) });
    } else {
      setFormState({ ...formState, program_ids: [...current, programId] });
    }
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'video': return <Video className="w-6 h-6 text-pink-600" />;
      case 'pdf': return <FileText className="w-6 h-6 text-red-600" />;
      case 'image': return <Image className="w-6 h-6 text-blue-600" />;
      default: return <FolderOpen className="w-6 h-6 text-zinc-400" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getLevelBadge = (level) => {
    const colors = {
      'free': 'bg-green-100 text-green-700',
      'nivel_1': 'bg-blue-100 text-blue-700',
      'nivel_2': 'bg-purple-100 text-purple-700',
      'nivel_3': 'bg-orange-100 text-orange-700',
      'premium': 'bg-yellow-100 text-yellow-700'
    };
    const labels = {
      'free': 'Gratis',
      'nivel_1': 'Nivel 1',
      'nivel_2': 'Nivel 2',
      'nivel_3': 'Nivel 3',
      'premium': 'Premium'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[level] || 'bg-zinc-800 text-zinc-300'}`}>
        {labels[level] || level}
      </span>
    );
  };

  const filteredContents = contents.filter(c => 
    c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold">Gestión de Contenido</h1>
            <p className="text-zinc-400 mt-1">Sube y administra videos, PDFs e imágenes</p>
          </div>
          <Button 
            onClick={() => setShowUploadModal(true)}
            className="bg-pink-600 hover:bg-pink-700 text-white rounded-full px-6"
          >
            <Plus className="w-5 h-5 mr-2" />
            Subir Contenido
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-zinc-900 rounded-2xl p-4 border border-zinc-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-pink-100 rounded-xl flex items-center justify-center">
                  <Video className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_videos}</p>
                  <p className="text-xs text-zinc-500">Videos</p>
                </div>
              </div>
            </div>
            <div className="bg-zinc-900 rounded-2xl p-4 border border-zinc-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_pdfs}</p>
                  <p className="text-xs text-zinc-500">PDFs</p>
                </div>
              </div>
            </div>
            <div className="bg-zinc-900 rounded-2xl p-4 border border-zinc-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Image className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_images}</p>
                  <p className="text-xs text-zinc-500">Imágenes</p>
                </div>
              </div>
            </div>
            <div className="bg-zinc-900 rounded-2xl p-4 border border-zinc-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <FolderOpen className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_files}</p>
                  <p className="text-xs text-zinc-500">Total</p>
                </div>
              </div>
            </div>
            <div className="bg-zinc-900 rounded-2xl p-4 border border-zinc-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Upload className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_size_mb}</p>
                  <p className="text-xs text-zinc-500">MB usados</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-zinc-900 rounded-2xl p-4 border border-zinc-800 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar contenido..."
                className="pl-10 rounded-xl"
              />
            </div>
            <select
              value={filter.type}
              onChange={(e) => setFilter({ ...filter, type: e.target.value })}
              className="px-4 py-2 border border-zinc-700 rounded-xl bg-zinc-800 text-white"
            >
              <option value="">Todos los tipos</option>
              <option value="video">Videos</option>
              <option value="pdf">PDFs</option>
              <option value="image">Imágenes</option>
            </select>
            <select
              value={filter.category}
              onChange={(e) => setFilter({ ...filter, category: e.target.value })}
              className="px-4 py-2 border border-zinc-700 rounded-xl bg-zinc-800 text-white"
            >
              <option value="">Todas las categorías</option>
              <option value="entrenamiento">Entrenamiento</option>
              <option value="nutricion">Nutrición</option>
              <option value="recetas">Recetas</option>
              <option value="ebook">Ebook</option>
              <option value="academia">Academia</option>
            </select>
            <select
              value={filter.level}
              onChange={(e) => setFilter({ ...filter, level: e.target.value })}
              className="px-4 py-2 border border-zinc-700 rounded-xl bg-zinc-800 text-white"
            >
              <option value="">Todos los niveles</option>
              <option value="free">Gratis</option>
              <option value="nivel_1">Nivel 1</option>
              <option value="nivel_2">Nivel 2</option>
              <option value="nivel_3">Nivel 3</option>
              <option value="premium">Premium</option>
            </select>
          </div>
        </div>

        {/* Content List */}
        <div className="space-y-4">
          {filteredContents.length === 0 ? (
            <div className="bg-zinc-900 rounded-3xl p-12 text-center">
              <Upload className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">No hay contenido</h3>
              <p className="text-zinc-500 mb-6">Sube tu primer archivo para comenzar</p>
              <Button 
                onClick={() => setShowUploadModal(true)}
                className="bg-pink-600 hover:bg-pink-700 text-white rounded-full"
              >
                <Plus className="w-5 h-5 mr-2" />
                Subir Contenido
              </Button>
            </div>
          ) : (
            filteredContents.map((content) => (
              <div key={content.content_id} className="bg-zinc-900 rounded-2xl p-4 border border-zinc-800 hover:border-zinc-700 transition-shadow">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-zinc-800 rounded-xl flex items-center justify-center flex-shrink-0">
                    {getFileIcon(content.content_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold truncate">{content.title}</h3>
                      {getLevelBadge(content.level)}
                      <span className="text-xs text-zinc-400 bg-zinc-800 px-2 py-0.5 rounded-full">
                        {content.category}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-500 truncate">{content.description || 'Sin descripción'}</p>
                    <div className="flex items-center gap-4 mt-1 text-xs text-zinc-400">
                      <span>{content.file_name}</span>
                      <span>{formatFileSize(content.file_size)}</span>
                      <span>{new Date(content.created_at).toLocaleDateString('es-ES')}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {content.content_type === 'video' && (
                      <a 
                        href={`${BACKEND_URL}${content.file_path}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="p-2 hover:bg-zinc-800 rounded-lg text-zinc-400"
                      >
                        <Video className="w-5 h-5" />
                      </a>
                    )}
                    {content.content_type === 'pdf' && (
                      <a 
                        href={`${BACKEND_URL}${content.file_path}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="p-2 hover:bg-zinc-800 rounded-lg text-zinc-400"
                      >
                        <FileText className="w-5 h-5" />
                      </a>
                    )}
                    {content.content_type === 'image' && (
                      <a 
                        href={`${BACKEND_URL}${content.file_path}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="p-2 hover:bg-zinc-800 rounded-lg text-zinc-400"
                      >
                        <Image className="w-5 h-5" />
                      </a>
                    )}
                    <button 
                      onClick={() => setEditingContent(content)}
                      className="p-2 hover:bg-zinc-800 rounded-lg text-zinc-400"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => handleDelete(content.content_id)}
                      className="p-2 hover:bg-red-50 rounded-lg text-red-600"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-3xl w-full max-w-lg overflow-hidden">
            <div className="p-6 border-b border-zinc-100">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Subir Contenido</h2>
                <button 
                  onClick={() => { setShowUploadModal(false); setUploadForm({ title: '', description: '', category: 'entrenamiento', level: 'free', file: null }); }}
                  className="p-2 hover:bg-zinc-800 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* File Drop Zone */}
              <div 
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-colors ${
                  uploadForm.file ? 'border-green-300 bg-green-50' : 'border-zinc-700 hover:border-pink-300 hover:bg-pink-50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*,application/pdf,image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                {uploadForm.file ? (
                  <div className="flex items-center justify-center gap-3">
                    <Check className="w-8 h-8 text-green-600" />
                    <div className="text-left">
                      <p className="font-medium text-green-700">{uploadForm.file.name}</p>
                      <p className="text-sm text-green-600">{formatFileSize(uploadForm.file.size)}</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="w-12 h-12 text-zinc-400 mx-auto mb-4" />
                    <p className="font-medium text-zinc-300">Haz clic para seleccionar un archivo</p>
                    <p className="text-sm text-zinc-500 mt-1">Videos (10GB), PDFs (50MB), Imágenes (10MB)</p>
                  </>
                )}
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium mb-2">Título *</label>
                <Input
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  placeholder="Título del contenido"
                  className="rounded-xl"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-2">Descripción</label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  placeholder="Descripción opcional..."
                  rows={3}
                  className="w-full px-4 py-2 border rounded-xl resize-none"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium mb-2">Categoría</label>
                <select
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
                  className="w-full px-4 py-2 border border-zinc-700 rounded-xl bg-zinc-800 text-white"
                >
                  <option value="entrenamiento">Entrenamiento</option>
                  <option value="nutricion">Nutrición</option>
                  <option value="recetas">Recetas</option>
                  <option value="ebook">Ebook</option>
                  <option value="academia">Academia</option>
                </select>
              </div>

              {/* Nivel de acceso y Opciones de Acceso removidos — se controla desde "Programas Vinculados" */}

              {/* Price & Discount - Solo visible si "paid" está seleccionado */}
              {uploadForm.access_options.includes('paid') && (
                <div className="grid grid-cols-3 gap-4 p-4 bg-blue-50 rounded-xl">
                  <div>
                    <label className="block text-sm font-medium mb-2">Precio ($) *</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={uploadForm.price}
                      onChange={(e) => setUploadForm({ ...uploadForm, price: e.target.value })}
                      placeholder="0.00"
                      className="rounded-xl bg-zinc-800 border-zinc-700 text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Código Descuento</label>
                    <Input
                      value={uploadForm.discount_code}
                      onChange={(e) => setUploadForm({ ...uploadForm, discount_code: e.target.value.toUpperCase() })}
                      placeholder="Ej: VERANO25"
                      className="rounded-xl bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">% Descuento</label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={uploadForm.discount_percentage}
                      onChange={(e) => setUploadForm({ ...uploadForm, discount_percentage: e.target.value })}
                      placeholder="0-100"
                      className="rounded-xl bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                </div>
              )}

              {/* Restricción por programa (opcional) */}
              <div>
                <label className="block text-sm font-medium mb-2 text-white">
                  Programas Vinculados <span className="text-zinc-500 font-normal">(opcional — deja vacío para usar reglas de nivel)</span>
                </label>
                {programs.length === 0 ? (
                  <p className="text-xs text-zinc-500">No hay programas configurados.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border border-zinc-700 rounded-xl bg-zinc-800/50">
                    {programs.map(prog => (
                      <label
                        key={prog.program_id}
                        className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all text-sm ${
                          uploadForm.program_ids?.includes(prog.program_id)
                            ? 'bg-pink-500/20 border border-pink-500/50 text-white'
                            : 'bg-zinc-900 border border-zinc-700 text-zinc-300 hover:border-pink-500/30'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={uploadForm.program_ids?.includes(prog.program_id) || false}
                          onChange={() => toggleProgramSelection(uploadForm, setUploadForm, prog.program_id)}
                          className="w-4 h-4 accent-pink-600"
                          data-testid={`upload-program-${prog.program_id}`}
                        />
                        <span className="truncate">{prog.name || prog.title || prog.program_id}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div className="bg-pink-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-pink-700">Subiendo...</span>
                    <span className="text-sm font-bold text-pink-700">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-pink-200 rounded-full h-2">
                    <div 
                      className="bg-pink-600 h-2 rounded-full transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Submit */}
              <Button
                onClick={handleUpload}
                disabled={uploading || !uploadForm.file}
                className="w-full bg-pink-600 hover:bg-pink-700 text-white rounded-full py-6 font-bold disabled:opacity-50"
              >
                {uploading ? 'Subiendo...' : 'Subir Contenido'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingContent && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-3xl w-full max-w-lg overflow-hidden">
            <div className="p-6 border-b border-zinc-100">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Editar Contenido</h2>
                <button 
                  onClick={() => setEditingContent(null)}
                  className="p-2 hover:bg-zinc-800 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Título</label>
                <Input
                  value={editingContent.title}
                  onChange={(e) => setEditingContent({ ...editingContent, title: e.target.value })}
                  className="rounded-xl"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Descripción</label>
                <textarea
                  value={editingContent.description || ''}
                  onChange={(e) => setEditingContent({ ...editingContent, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border rounded-xl resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Categoría</label>
                <select
                  value={editingContent.category}
                  onChange={(e) => setEditingContent({ ...editingContent, category: e.target.value })}
                  className="w-full px-4 py-2 border border-zinc-700 rounded-xl bg-zinc-800 text-white"
                >
                  <option value="entrenamiento">Entrenamiento</option>
                  <option value="nutricion">Nutrición</option>
                  <option value="recetas">Recetas</option>
                  <option value="ebook">Ebook</option>
                  <option value="academia">Academia</option>
                </select>
              </div>

              {/* Nivel de acceso removido — se controla desde "Programas Vinculados" */}

              {/* Restricción por programa en edición */}
              <div>
                <label className="block text-sm font-medium mb-2 text-white">
                  Programas Vinculados <span className="text-zinc-500 font-normal">(opcional)</span>
                </label>
                {programs.length === 0 ? (
                  <p className="text-xs text-zinc-500">No hay programas configurados.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border border-zinc-700 rounded-xl bg-zinc-800/50">
                    {programs.map(prog => (
                      <label
                        key={prog.program_id}
                        className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all text-sm ${
                          editingContent.program_ids?.includes(prog.program_id)
                            ? 'bg-pink-500/20 border border-pink-500/50 text-white'
                            : 'bg-zinc-900 border border-zinc-700 text-zinc-300 hover:border-pink-500/30'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={editingContent.program_ids?.includes(prog.program_id) || false}
                          onChange={() => toggleProgramSelection(editingContent, setEditingContent, prog.program_id)}
                          className="w-4 h-4 accent-pink-600"
                          data-testid={`edit-program-${prog.program_id}`}
                        />
                        <span className="truncate">{prog.name || prog.title || prog.program_id}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm text-white">
                <input
                  type="checkbox"
                  checked={editingContent.active !== false}
                  onChange={(e) => setEditingContent({ ...editingContent, active: e.target.checked })}
                  className="w-4 h-4 accent-pink-600"
                  data-testid="edit-content-active"
                />
                Contenido activo (visible para usuarios)
              </label>

              <Button
                onClick={handleUpdate}
                className="w-full bg-pink-600 hover:bg-pink-700 text-white rounded-full py-6 font-bold"
              >
                Guardar Cambios
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContent;
